import { toast } from 'react-toastify';
import { getFormData, ResponseStatus } from '../app/services/ApiHelper';
import {
    ServiceParams,
    ServiceParamsDocuments,
    ServiceResponse
} from '../app/services/shared/interfaces';
import { getFetchDefaultHeaders } from '../app/services/shared/vars';
import { LOCAL_STORAGE } from '../config';

// Rutas que se abren sin sesión. Un 401 en ellas no significa que la sesión expiró,
// así que expulsar al login rompe el flujo: le pasaba al firmante que abría el enlace
// del email con un token viejo guardado en el navegador.
const PUBLIC_PATHS = [
    '/login', '/recovery-password', '/activate-account', '/sign/',
    '/firmar-poder/', '/invitacion-estudio/', '/registro-abogados'
];

const isPublicPage = () =>
    PUBLIC_PATHS.some((path) => window.location.pathname.startsWith(path));

const useFetch = () => {
    const doGet = (serviceParams: ServiceParams) => {
        abstractFetch('GET', serviceParams);
    };

    const doPost = (serviceParams: ServiceParams) => {
        abstractFetch('POST', serviceParams);
    };

    const doPut = (serviceParams: ServiceParams) => {
        abstractFetch('PUT', serviceParams);
    };

    const doDelete = (serviceParams: ServiceParams) => {
        abstractFetch('DELETE', serviceParams);
    };

    const doGetDocument = (serviceParams: ServiceParamsDocuments) => {
        abstractFetchDocument('GET', serviceParams);
    };


    const abstractFetch = (method: string, serviceParams: ServiceParams) => {
        const {
            url,
            body,
            headers,
            requestType,
            onSuccess,
            onError,
            onInfo,
            onWarning,
            onFieldError,
            setFetching
        } = serviceParams;

        let httpStatus = 0;

        if (setFetching) {
            setFetching(true);
        }

        const fetchDefaultHeaders = getFetchDefaultHeaders();
        
        // Agregar Content-Type cuando es JSON
        const finalHeaders = {
            ...fetchDefaultHeaders,
            ...(requestType === 'json' ? { 'Content-Type': 'application/json' } : {}),
            ...headers
        };
        
        // Preparar el body según el tipo
        let processedBody = null;
        if (body) {
            if (requestType === 'json') {
                processedBody = JSON.stringify(body);
            } else if (body instanceof FormData) {
                // Si ya es FormData, usarlo directamente
                processedBody = body;
            } else {
                // Convertir objeto a FormData
                processedBody = getFormData(body);
            }
        }
        
        fetch(url, {
            method: method,
            headers: finalHeaders,
            body: processedBody
        })
            .then((response) => {
                httpStatus = response.status;
                // console.log(response.headers.get("Content-Type"))
                return response.json();
            })
            .then((response) => {
                if (httpStatus === 401 && !isPublicPage()) {
                    localStorage.removeItem(LOCAL_STORAGE.AUTH);
                    localStorage.removeItem(LOCAL_STORAGE.AUTH_TOKEN);

                    window.location.href = '/login';
                }

                response.http_status = httpStatus;

                handleResponse(response, onSuccess, onError, onInfo, onWarning, onFieldError);
                if (setFetching) {
                    setFetching(false);
                }
            })
            .catch(() => {
                // Error de red, o respuesta que no es JSON (413, 502, página HTML):
                // se avisa como error para que la pantalla no quede colgada.
                if (setFetching) {
                    setFetching(false);
                }
                if (onError) {
                    onError({
                        status: 'error',
                        title: 'Error',
                        message: httpStatus === 413
                            ? 'El archivo es demasiado grande para el servidor.'
                            : 'No se pudo conectar con el servidor. Intenta de nuevo.',
                        data: null,
                        http_status: httpStatus
                    });
                }
            });
    };

    const abstractFetchDocument = (method: 'GET', serviceParams: ServiceParamsDocuments) => {
        const {
            url,
            headers,
            onSuccess,
            onError,
            onInfo,
            onWarning,
            onFieldError,
            setFetching,
            successData = {
                nameDocument: 'document',
                message: 'Documento recuperado con éxito'
            }
        } = serviceParams;

        let httpStatus = 0;

        if (setFetching) {
            setFetching(true);
        }

        const fetchDefaultHeaders = getFetchDefaultHeaders();

        fetch(url, {
            method: method,
            headers: { ...fetchDefaultHeaders, ...headers }
        })
            .then((response) => {
                httpStatus = response.status;

                if (response.ok) {
                    return response.blob();
                }
                return response.json();
            })
            .then((data) => {
                if (httpStatus === 401 && !isPublicPage()) {
                    localStorage.removeItem(LOCAL_STORAGE.AUTH);
                    localStorage.removeItem(LOCAL_STORAGE.AUTH_TOKEN);

                    window.location.href = '/login';
                }

                if (httpStatus === 200) {
                    const blobURL = URL.createObjectURL(data);
                    const a = document.createElement('a');
                    a.href = blobURL;
                    // Contratos antiguos se guardaron en Word: la extensión sigue al archivo real
                    let downloadName: string = successData.nameDocument;
                    if (data.type?.includes('wordprocessingml') && downloadName.endsWith('.pdf')) {
                        downloadName = downloadName.replace(/\.pdf$/, '.docx');
                    }
                    a.download = downloadName;
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);

                    URL.revokeObjectURL(blobURL);

                    if (onSuccess) onSuccess(successData.message);
                } else {
                    if (onError) onError(data);
                }

                if (setFetching) {
                    setFetching(false);
                }
            })
            .catch((e) => {
                console.log(e);
                if (setFetching) {
                    setFetching(false);
                }
            });
    };


    const handleResponse = (
        response: ServiceResponse,
        onSuccess?: (response: ServiceResponse) => void,
        onError?: (response: ServiceResponse) => void,
        onInfo?: (response: ServiceResponse) => void,
        onWarning?: (response: ServiceResponse) => void,
        onFieldError?: (response: ServiceResponse) => void
    ) => {
        switch (response.status) {
            case ResponseStatus.SUCCESS:
                if (onSuccess) {
                    onSuccess(response);
                }
                break;
            case ResponseStatus.ERROR:
                if (onError) {
                    onError(response);
                }
                break;
            case ResponseStatus.INFO:
                if (onInfo) {
                    onInfo(response);
                }
                break;
            case ResponseStatus.WARNING:
                if (onWarning) {
                    onWarning(response);
                }
                break;
            case ResponseStatus.FIELDS_VALIDATION:
                if (onFieldError) {
                    onFieldError(response);
                }
                break;
            default:
                if (onError) {
                    onError(response);
                }
                break;
        }
    };

    return {
        doGet,
        doPost,
        doPut,
        doDelete,
        doGetDocument,
    };
};

export default useFetch;
